export function getChristmasPreorderPlan(variantId, stoq = globalThis._RestockRocket) {
  const plan = stoq?.getSellingPlan?.(Number(variantId));
  if (!plan?.enabled || plan.payment_type !== 'full' || !plan.variant_ids?.some(id => String(id) === String(variantId)) || !/^\d+$/.test(String(plan.shopify_selling_plan_id))) throw new Error('closed');
  return String(plan.shopify_selling_plan_id);
}

export async function addChristmasBundle(bundle, fetcher = globalThis.fetch) {
  const { book, audiobook, card, letters, bookPlan, cardPlan, addUrl } = bundle;
  if ([book, audiobook, card, letters, bookPlan, cardPlan].some(id => !/^\d+$/.test(String(id)))) throw new Error('closed');

  const added = await fetcher(addUrl, {
    method: 'POST',
    credentials: 'same-origin',
    signal: AbortSignal.timeout(15000),
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ items: [
      { id: book, quantity: 1, selling_plan: bookPlan },
      { id: audiobook, quantity: 1 },
      { id: card, quantity: 1, selling_plan: cardPlan },
      { id: letters, quantity: 1 }
    ] })
  });
  if (!added.ok) throw new Error('add');
  const result = await added.json();
  if (result.status || !Array.isArray(result.items) || result.items.length !== 4) throw new Error('add');
  return result;
}

export function setupBundleOrder(landing, addBundle = addChristmasBundle, getPlan = getChristmasPreorderPlan, navigate = destination => window.location.assign(destination)) {
  const order = landing.querySelector('chlopiec-bundle-order');
  const buttons = [...landing.querySelectorAll('[data-chlopiec-bundle-submit]')];
  const errors = landing.querySelectorAll('[data-chlopiec-bundle-error]');
  if (!order || !buttons.length || !errors.length || order.dataset.enabled !== 'true') return;

  buttons.forEach(button => {
    button.disabled = false;
    button.addEventListener('click', async () => {
      if (button.disabled) return;
      buttons.forEach(control => {
        control.disabled = true;
        control.setAttribute('aria-busy', 'true');
      });
      errors.forEach(error => { error.textContent = ''; });
      try {
        await addBundle({
          book: order.dataset.bookId,
          audiobook: order.dataset.audiobookId,
          card: order.dataset.cardId,
          letters: order.dataset.lettersId,
          bookPlan: getPlan(order.dataset.bookId),
          cardPlan: getPlan(order.dataset.cardId),
          addUrl: order.dataset.addUrl
        });
        navigate(order.dataset.destination);
      } catch (failure) {
        const message = failure.message === 'closed' ? order.dataset.closedError : order.dataset.requestError;
        const error = button.parentElement.querySelector('[data-chlopiec-bundle-error]');
        if (error) error.textContent = message;
        buttons.forEach(control => {
          control.disabled = false;
          control.removeAttribute('aria-busy');
        });
      }
    });
  });
}

export function getCountdownParts(deadline, now = Date.now()) {
  const target = Date.parse(deadline);
  if (!Number.isFinite(target)) return null;
  const remaining = Math.max(0, Math.ceil((target - now) / 1000));
  return {
    days: Math.floor(remaining / 86400),
    hours: Math.floor(remaining % 86400 / 3600),
    minutes: Math.floor(remaining % 3600 / 60),
    seconds: remaining % 60,
    expired: remaining === 0
  };
}

if (globalThis.document) {
  const setupCountdown = (root) => {
    const countdowns = root.matches?.('[data-chlopiec-countdown]') ? [root] : root.querySelectorAll('[data-chlopiec-countdown]');
    countdowns.forEach((countdown) => {
      if (countdown.dataset.initialized) return;
      countdown.dataset.initialized = 'true';
      const clock = countdown.querySelector('.chlopiec__countdown-clock');
      const expired = countdown.querySelector('[data-countdown-expired]');
      const update = () => {
        if (!countdown.isConnected) {
          clearInterval(interval);
          return;
        }
        const parts = getCountdownParts(countdown.dataset.deadline);
        if (!parts) {
          clock.hidden = true;
          clearInterval(interval);
          return;
        }
        for (const unit of ['days', 'hours', 'minutes', 'seconds']) {
          countdown.querySelector(`[data-countdown-value="${unit}"]`).textContent = String(parts[unit]).padStart(2, '0');
        }
        clock.hidden = parts.expired;
        expired.hidden = !parts.expired;
        if (parts.expired) clearInterval(interval);
      };
      const interval = setInterval(update, 1000);
      update();
    });
  };

  setupCountdown(document);
  document.addEventListener('shopify:section:load', (event) => setupCountdown(event.target));

  const setupDedicationCounter = (landing) => {
    const url = landing.dataset.chlopiecCounterUrl;
    const template = landing.dataset.chlopiecCounterTemplate;
    const outputs = landing.querySelectorAll('[data-chlopiec-counter-remaining]');
    if (!url || !template?.includes('{remaining}') || !outputs.length) return;

    const update = async () => {
      try {
        const requestUrl = new URL(url);
        if (requestUrl.protocol !== 'https:') throw new Error('Invalid counter URL');
        requestUrl.searchParams.set('_', String(Date.now()));
        const response = await fetch(requestUrl, { cache: 'no-store', signal: AbortSignal.timeout(10000) });
        if (!response.ok) throw new Error('Counter unavailable');
        const remaining = (await response.text()).replace(/^\uFEFF/, '').trim();
        if (!/^\d{1,3}$/.test(remaining) || Number(remaining) > 500) throw new Error('Invalid counter value');
        outputs.forEach((output) => {
          output.textContent = template.replace('{remaining}', remaining);
          output.hidden = false;
        });
      } catch {
        outputs.forEach((output) => { output.hidden = true; });
      }
    };

    update();
    const timer = setInterval(update, 5 * 60 * 1000);
    document.addEventListener('shopify:section:unload', (event) => {
      if (event.target?.contains(landing)) clearInterval(timer);
    });
  };

  const setupImageZoom = (landing) => {
    const dialog = landing.querySelector('.chlopiec__dialog');
    const dialogImage = dialog?.querySelector('img');
    if (!dialog || !dialogImage || typeof dialog.showModal !== 'function') return;

    const openImage = (source, alt) => {
      dialogImage.src = source;
      dialogImage.alt = alt;
      dialog.showModal();
    };

    landing.querySelectorAll('[data-chlopiec-image]').forEach((button) => {
      button.addEventListener('click', () => openImage(button.dataset.chlopiecImage, button.dataset.chlopiecAlt || ''));
    });

    const mobile = window.matchMedia('(max-width: 749px)');
    const images = [...landing.querySelectorAll('img')].filter((image) =>
      !image.closest('.chlopiec__dialog, .chlopiec__image-button') && !image.classList.contains('chlopiec__story-stars')
    );
    const updateMobileImages = () => {
      images.forEach((image) => {
        if (mobile.matches) {
          image.setAttribute('role', 'button');
          image.tabIndex = 0;
          image.setAttribute('aria-label', `${landing.dataset.chlopiecZoomLabel}: ${image.alt}`);
          image.dataset.chlopiecMobileZoom = '';
        } else {
          image.removeAttribute('role');
          image.removeAttribute('tabindex');
          image.removeAttribute('aria-label');
          delete image.dataset.chlopiecMobileZoom;
        }
      });
    };

    images.forEach((image) => {
      const zoom = () => {
        if (!mobile.matches) return;
        const source = image.closest('[data-chlopiec-full-image]')?.dataset.chlopiecFullImage || image.src;
        openImage(source, image.alt);
      };
      image.addEventListener('click', zoom);
      image.addEventListener('keydown', (event) => {
        if (mobile.matches && (event.key === 'Enter' || event.key === ' ')) {
          event.preventDefault();
          zoom();
        }
      });
    });

    mobile.addEventListener('change', updateMobileImages);
    updateMobileImages();
    dialog.querySelector('.chlopiec__dialog-close')?.addEventListener('click', () => dialog.close());
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) dialog.close();
    });
  };

  document.querySelectorAll('.chlopiec').forEach((landing) => {
    setupDedicationCounter(landing);
    setupImageZoom(landing);
    setupBundleOrder(landing);
  });
}
